import {styled} from '@mui/material/styles';
import type {TypographyProps as MaterialTypographyProps} from '@mui/material/Typography';
import MaterialTypography from '@mui/material/Typography';
import React from 'react';
import type {FuelSize} from '../types/general';

type FuelTypographyFontWeight = 'regular' | 'semibold' | 'bold';

type FuelTypographySize = 'xs' | FuelSize | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl';

type FuelTypographyColor = 'initial' | 'inherit' | 'primary' | 'secondary' | 'tertiary' | 'error';

type FuelTypographyVariant = 'label' | MaterialTypographyProps['variant'] | 'visuallyHidden';

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        label: true;
        visuallyHidden: true;
    }
}

export interface TypographyProps extends Omit<MaterialTypographyProps,
'color' |
'variant'
> {
    /* Font Size */
    size?: FuelTypographySize;
    /* Font weight */
    fontWeight?: FuelTypographyFontWeight;
    /* Color */
    color?: FuelTypographyColor;
    /* Variants including custom Fuel additions */
    variant?: FuelTypographyVariant;
    /**
     * Used to replace root node with different
     * component.
     */
    component?: React.ElementType<any>;
}

const StyledTypography = styled(MaterialTypography)<TypographyProps>(({
    theme, size, fontWeight, color, variant
}) => ({
    ...color === 'initial' && {
        color: 'initial'
    },
    ...color === 'inherit' && {
        color: 'inherit'
    },
    ...color === 'primary' && {
        color: theme.tokens.FuelColorTextPrimary
    },
    ...color === 'secondary' && {
        color: theme.tokens.FuelColorTextSecondary
    },
    ...color === 'tertiary' && {
        color: theme.tokens.FuelColorTextTertiary
    },
    ...color === 'error' && {
        color: '#c21020'
    },

    ...size === 'xs' && {
        fontSize: theme.tokens.FuelFontSizeXs,
        lineHeight: theme.tokens.FuelLineHeightXs
    },
    ...(size === 'sm' || size === 'small') && {
        fontSize: theme.tokens.FuelFontSizeSm,
        lineHeight: theme.tokens.FuelLineHeightSm
    },
    ...(size === 'md' || size === 'medium') && {
        fontSize: theme.tokens.FuelFontSizeMd,
        lineHeight: theme.tokens.FuelLineHeightMd
    },
    ...(size === 'lg' || size === 'large') && {
        fontSize: theme.tokens.FuelFontSizeLg,
        lineHeight: theme.tokens.FuelLineHeightLg
    },
    ...size === 'xl' && {
        fontSize: theme.tokens.FuelFontSizeXl,
        lineHeight: theme.tokens.FuelLineHeightXl
    },
    ...size === '2xl' && {
        fontSize: theme.tokens.FuelFontSize2xl,
        lineHeight: theme.tokens.FuelLineHeight2xl
    },
    ...size === '3xl' && {
        fontSize: theme.tokens.FuelFontSize3xl,
        lineHeight: theme.tokens.FuelLineHeight3xl
    },
    ...size === '4xl' && {
        fontSize: theme.tokens.FuelFontSize4xl,
        lineHeight: theme.tokens.FuelLineHeight4xl
    },
    ...size === '5xl' && {
        fontSize: theme.tokens.FuelFontSize5xl,
        lineHeight: theme.tokens.FuelLineHeight5xl
    },
    ...size === '6xl' && {
        fontSize: theme.tokens.FuelFontSize6xl,
        lineHeight: theme.tokens.FuelLineHeight6xl
    },

    // TODO: avoid TS errors by converting fontWeight value to number
    ...fontWeight === 'regular' && {
        fontWeight: parseInt(theme.tokens.FuelFontWeightRegular, 10)
    },
    ...fontWeight === 'semibold' && {
        fontWeight: parseInt(theme.tokens.FuelFontWeightSemibold, 10)
    },
    ...fontWeight === 'bold' && {
        fontWeight: parseInt(theme.tokens.FuelFontWeightBold, 10)
    },

    // exceptions
    ...variant === 'label' && {
        color: theme.tokens.FuelColorGray900,
        fontWeight: parseInt(theme.tokens.FuelFontWeightSemibold, 10),
        fontSize: theme.tokens.FuelFontSizeSm,
        lineHeight: theme.tokens.FuelLineHeightSm
    },
    ...variant === 'visuallyHidden' && {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: '1px',
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        whiteSpace: 'nowrap',
        width: '1px'
    }
}));

export const Typography = React.forwardRef<any, TypographyProps>(
    (
        {
            children,
            variant = 'body1',
            size,
            fontWeight,
            color,
            ...props
        },
        ref
    ) => (
        <StyledTypography
            color={color}
            fontWeight={fontWeight}
            ref={ref}
            size={size}
            variant={variant}
            {...props}
        >
            {children}
        </StyledTypography>
    )
);

Typography.displayName = 'Typography';
